import { Link } from 'react-router-dom';
import { testimonials } from '@/mocks/posts';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import { useSeo } from '@/hooks/useSeo';
import { site } from '@/utils/site';

const principles = [
  {
    icon: 'ri-time-line',
    title: '短，但不缺信息',
    desc: '每期合集控制在你通勤路上能看完的长度，同时把前因后果交代清楚，不做无信息量的拼接。',
  },
  {
    icon: 'ri-focus-3-line',
    title: '只挑真正高能的',
    desc: '时长不是标准，情绪浓度和信息密度才是。宁可选少一点，也不把没看点的片段凑成合集。',
  },
  {
    icon: 'ri-scales-3-line',
    title: '就事论事',
    desc: '我们只讨论公开播出内容里的表现与细节，不做没有依据的延伸揣测，也不搬运未经证实的说法。',
  },
  {
    icon: 'ri-refresh-line',
    title: '有错就改',
    desc: '接到纠错反馈后我们会第一时间核实并修订，并在页面更新中说明调整内容，不悄悄改掉。',
  },
];

export default function AboutPage() {
  useSeo({
    title: '关于我们_黑料看点的定位与内容原则',
    description:
      '了解黑料看点：一个专注高能名场面合集的内容门户。我们坚持短而不缺信息、只挑真正高能片段、就事论事与有错就改的内容原则。',
    keywords: '黑料看点介绍,黑料看点团队,黑料看点内容原则,高能名场面合集',
    path: '/about',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: '关于黑料看点',
        description: '黑料看点的品牌介绍、内容原则与联系方式。',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: site.name,
        description: site.description,
        email: site.email,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
          { '@type': 'ListItem', position: 2, name: '关于我们' },
        ],
      },
    ],
  });

  return (
    <SiteLayout>
      <section className="w-full px-4 md:px-8 pt-24 md:pt-32 pb-10 md:pb-14 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <Breadcrumbs items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
          <div className="mt-5 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-7">
              <h1 className="font-heading text-3xl md:text-5xl text-foreground-950 leading-tight">
                关于黑料看点
              </h1>
              <p className="mt-5 text-sm md:text-base text-foreground-700 leading-8">
                黑料看点是一个专注高能名场面合集的内容门户。我们不做信息流的搬运工，而是把公开播出的综艺、影视、颁奖礼、体坛和网络热梗里那些真正值得回看的片段挑出来，配上必要的背景交代，让你点开就能抓住核心。
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="bg-background-50 rounded-lg border border-background-200 p-6 grid grid-cols-3 gap-4">
                {[
                  { value: '320+', label: '收录合集' },
                  { value: '5', label: '内容方向' },
                  { value: '2 天', label: '反馈响应' },
                ].map((item) => (
                  <div key={item.label} className="text-center">
                    <div className="font-heading text-2xl md:text-3xl text-primary-600">{item.value}</div>
                    <div className="mt-1 text-[11px] text-foreground-500">{item.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-14 md:py-20">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-1.5 text-xs font-label tracking-[0.2em] text-primary-600 uppercase mb-3">
              <i className="ri-subtract-line" />
              我们的定位
            </span>
            <h2 className="font-heading text-2xl md:text-4xl text-foreground-950">为什么要有这样一个站</h2>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-8">
              高能名场面从来不缺，缺的是"帮你看完再告诉你重点在哪"的人。我们想做的，就是把这一层筛选和梳理做扎实。
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {principles.map((item) => (
              <div
                key={item.title}
                className="bg-background-50 rounded-lg border border-background-200 p-6 hover:border-primary-300 transition-colors"
              >
                <span className="w-11 h-11 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-900 text-xl">
                  <i className={item.icon} />
                </span>
                <h3 className="mt-4 font-heading text-lg text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm text-foreground-600 leading-7">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-14 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14">
          <div className="lg:col-span-5">
            <span className="inline-flex items-center gap-1.5 text-xs font-label tracking-[0.2em] text-primary-600 uppercase mb-3">
              <i className="ri-subtract-line" />
              发展理念
            </span>
            <h2 className="font-heading text-2xl md:text-4xl text-foreground-950 leading-tight">
              先做减法，再做整理
            </h2>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-8">
              内容越来越多，注意力却越来越少。我们相信，一个好的看点门户应该先帮你删掉不重要的部分，再把剩下的讲清楚。所以我们很少追求更新数量，而是把精力放在选题和事实核对上。
            </p>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-8">
              未来我们会继续把分类做细，增加更多可筛选的维度，也会开放更顺畅的推荐入口，让网友的安利能更快进入合集。
            </p>
          </div>
          <div className="lg:col-span-7">
            <div className="bg-background-50 rounded-lg border border-background-200 p-6 md:p-8">
              <h3 className="font-heading text-xl text-foreground-950">我们如何对待每一条内容</h3>
              <ol className="mt-5 space-y-5">
                {[
                  { step: '01', title: '筛选', desc: '从公开播出的节目与赛事中挑选情绪浓度高、有明确看点的片段。' },
                  { step: '02', title: '梳理', desc: '补齐背景脉络，用简洁的文字交代清楚前因后果，避免看不懂。' },
                  { step: '03', title: '核对', desc: '对涉及时间、比分、场次等客观信息做二次确认，减少出错。' },
                  { step: '04', title: '更新', desc: '收到反馈后及时修订，并把调整记录保留在页面中。' },
                ].map((item) => (
                  <li key={item.step} className="flex gap-4">
                    <span className="font-heading text-lg text-accent-600 shrink-0">{item.step}</span>
                    <span>
                      <span className="block text-sm font-medium text-foreground-900">{item.title}</span>
                      <span className="mt-1 block text-sm text-foreground-600 leading-7">{item.desc}</span>
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-14 md:py-20">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-heading text-2xl md:text-4xl text-foreground-950 text-center">网友的真实反馈</h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {testimonials.map((item) => (
              <figure key={item.name} className="bg-background-100 rounded-lg border border-background-200 p-6">
                <blockquote className="text-sm text-foreground-700 leading-7">“{item.text}”</blockquote>
                <figcaption className="mt-5 flex items-center gap-3">
                  <div className="w-10 h-10 shrink-0 overflow-hidden rounded-full bg-background-200">
                    <img src={item.avatar} alt={`${item.name}的头像`} className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-900">{item.name}</div>
                    <div className="text-xs text-foreground-500">{item.role}</div>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto rounded-lg bg-foreground-950 px-6 py-10 md:px-12 md:py-14 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
          <div className="max-w-2xl">
            <h2 className="font-heading text-2xl md:text-3xl text-background-50">和我们聊聊</h2>
            <p className="mt-3 text-sm md:text-base text-background-100/80 leading-8">
              无论是名场面推荐、内容纠错还是合作洽谈，都可以直接联系我们。你的每一条反馈，我们都会认真看。
            </p>
            <div className="mt-5 flex flex-wrap gap-x-8 gap-y-3 text-sm text-background-100/80">
              <span className="flex items-center gap-2">
                <i className="ri-mail-line" />
                {site.email}
              </span>
              <span className="flex items-center gap-2">
                <i className="ri-team-line" />
                {site.bizEmail}
              </span>
            </div>
          </div>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-400 transition-colors shrink-0"
          >
            <i className="ri-mail-send-line" />
            前往留言
          </Link>
        </div>
      </section>
    </SiteLayout>
  );
}